/**
 * Batched style-layer visibility updates for VectorTileLayers.
 *
 * ## Why this exists (JMKW-1859)
 *
 * `VectorTileLayer.setStyleLayerVisibility()` invalidates the layer's tile data on every
 * call. With a SceneView attached, each invalidation walks the 3D tile tree and re-requests
 * tiles. A JMKW theme switch issues ~3,844 of these calls across the navigation, obstacles,
 * and cultural layers, which retained ~700MB of re-requested tile data per switch and
 * crashed the renderer (out of memory) after roughly four switches.
 *
 * Measured on the reference app, dual 2D+3D view:
 *   - per-call:  ~7,500ms blocked, +700MB heap per theme switch, OOM at ~4 switches
 *   - batched:   ~200ms blocked, ~0MB heap per theme switch, no crash
 *
 * Callers queue visibility changes instead of applying them. Each affected layer is then
 * updated with a single `loadStyle()`, producing one invalidation per layer per theme
 * change rather than thousands.
 *
 * ## The absolute-URL requirement
 *
 * A style fetched from a service uses paths relative to the style document, e.g.
 * `sprite: "../sprites/sprite"`. `loadStyle(styleObject)` has no base URL to resolve those
 * against and throws. `currentStyleInfo` already carries resolved absolute URLs, so they are
 * substituted onto the clone before it is handed back.
 *
 * ## Safety
 *
 * If a layer cannot be batched — no `loadStyle`, no parsed style, or `loadStyle` rejects —
 * the queued changes are applied individually via `setStyleLayerVisibility()`. Correctness
 * is never traded for performance; the worst case is the original behavior.
 */

export type StyleLayerVisibility = 'none' | 'visible';

// NOTE: every optional field below is widened with `| null` to match how ArcGIS types
// `currentStyleInfo`. Narrowing these to `string | undefined` makes real VectorTileLayer
// instances structurally incompatible at the call site.

interface StyleLayerDefinition {
  id: string;
  layout?: Record<string, unknown> | null;
}

interface VectorTileStyle {
  layers?: StyleLayerDefinition[] | null;
  sources?: Record<string, { url?: string | null } | undefined | null> | null;
  sprite?: string | null;
  glyphs?: string | null;
}

interface CurrentStyleInfoLike {
  style?: VectorTileStyle | null;
  spriteUrl?: string | null;
  glyphsUrl?: string | null;
  serviceUrl?: string | null;
  sourceNameToSource?: Record<
    string,
    { url?: string | null } | undefined | null
  > | null;
}

/** Minimal surface required to batch — matches VectorTileLayer without depending on it. */
export interface BatchableStyleLayer {
  setStyleLayerVisibility?(
    layerId: string,
    visibility: StyleLayerVisibility
  ): void;
  loadStyle?(style: unknown): Promise<unknown>;
  currentStyleInfo?: CurrentStyleInfoLike | null;
  destroyed?: boolean;
}

interface BatchState {
  pending: Map<string, StyleLayerVisibility>;
  scheduled: boolean;
  /** Serializes flushes so overlapping async loadStyle calls cannot interleave. */
  chain: Promise<void>;
  /** Bumped on every queued change; see `styleBatchRevision`. */
  revision: number;
}

const BATCHES = new WeakMap<BatchableStyleLayer, BatchState>();

function getState(layer: BatchableStyleLayer): BatchState {
  let state = BATCHES.get(layer);
  if (!state) {
    state = {
      pending: new Map(),
      scheduled: false,
      chain: Promise.resolve(),
      revision: 0
    };
    BATCHES.set(layer, state);
  }
  return state;
}

function canBatch(layer: BatchableStyleLayer): boolean {
  return (
    typeof layer.loadStyle === 'function' &&
    Array.isArray(layer.currentStyleInfo?.style?.layers)
  );
}

function applyDirectly(
  layer: BatchableStyleLayer,
  batch: Map<string, StyleLayerVisibility>
): void {
  batch.forEach((visibility, id) => {
    try {
      layer.setStyleLayerVisibility?.(id, visibility);
    } catch {
      /* a single style layer failing must not abort the rest */
    }
  });
}

function cloneStyle(style: VectorTileStyle): VectorTileStyle {
  return typeof structuredClone === 'function'
    ? structuredClone(style)
    : (JSON.parse(JSON.stringify(style)) as VectorTileStyle);
}

/**
 * Replace style-relative references with the absolute URLs ArcGIS already resolved.
 * Without this, `loadStyle()` cannot resolve `../sprites/sprite` and throws.
 */
function absolutize(style: VectorTileStyle, info: CurrentStyleInfoLike): void {
  if (info.spriteUrl) style.sprite = info.spriteUrl;
  if (info.glyphsUrl) style.glyphs = info.glyphsUrl;
  if (!style.sources) return;
  Object.keys(style.sources).forEach((name) => {
    const source = style.sources?.[name];
    if (!source) return;
    const resolved = info.sourceNameToSource?.[name]?.url ?? info.serviceUrl;
    if (resolved) source.url = resolved;
  });
}

async function flushBatch(
  layer: BatchableStyleLayer,
  batch: Map<string, StyleLayerVisibility>
): Promise<void> {
  if (layer.destroyed) return;

  const info = layer.currentStyleInfo;
  const liveStyle = info?.style;
  if (!info || !Array.isArray(liveStyle?.layers)) {
    applyDirectly(layer, batch);
    return;
  }

  try {
    const style = cloneStyle(liveStyle);
    absolutize(style, info);

    let changed = 0;
    style.layers?.forEach((styleLayer) => {
      if (!batch.has(styleLayer.id)) return;
      const visibility = batch.get(styleLayer.id);
      const layout = styleLayer.layout ?? (styleLayer.layout = {});
      if (layout['visibility'] !== visibility) {
        layout['visibility'] = visibility;
        changed++;
      }
    });

    // Nothing actually differs from the live style — skip the invalidation entirely.
    if (changed === 0) return;

    await layer.loadStyle?.(style);
  } catch {
    // Never lose a style change to a batching failure.
    applyDirectly(layer, batch);
  }
}

function schedule(layer: BatchableStyleLayer, state: BatchState): void {
  if (state.scheduled) return;
  state.scheduled = true;
  const run = () => {
    state.scheduled = false;
    if (state.pending.size === 0) return;
    const batch = new Map(state.pending);
    state.pending.clear();
    state.chain = state.chain
      .then(() => flushBatch(layer, batch))
      .catch(() => {
        /* flushBatch already falls back; keep the chain alive */
      });
  };
  if (typeof queueMicrotask === 'function') queueMicrotask(run);
  else void Promise.resolve().then(run);
}

/**
 * Queue a style-layer visibility change. Changes for the same layer are coalesced and
 * applied together on the next microtask via a single `loadStyle()`.
 *
 * Drop-in replacement for `layer.setStyleLayerVisibility(id, visibility)` on any code path
 * that issues many calls at once — notably theme application.
 */
export function queueStyleLayerVisibility(
  layer: BatchableStyleLayer | null | undefined,
  styleLayerId: string,
  visibility: StyleLayerVisibility
): void {
  if (!layer || !styleLayerId) return;

  if (!canBatch(layer)) {
    try {
      layer.setStyleLayerVisibility?.(styleLayerId, visibility);
    } catch {
      /* preserve prior best-effort behavior */
    }
    return;
  }

  const state = getState(layer);
  state.pending.set(styleLayerId, visibility);
  state.revision++;
  schedule(layer, state);
}

/**
 * Monotonic revision of the batched changes queued for `layer`; 0 if it has never been
 * batched. Sample it before a styling pass and compare afterwards to learn whether that
 * pass went through this module.
 *
 * Callers use this to suppress work that `loadStyle()` already covers. `loadStyle()`
 * re-fetches the layer's tiles itself, so an additional invalidation — notably
 * `VectorTileLayer.refresh()` — only cancels those in-flight requests, which ArcGIS reports
 * as one "error refreshing vector-tiles layer-view" AbortError apiece (JMKW-1868).
 *
 * Only the batched path bumps the revision. When a layer cannot be batched the caller sees
 * no change and keeps whatever fallback behavior it had.
 */
export function styleBatchRevision(
  layer: BatchableStyleLayer | null | undefined
): number {
  if (!layer) return 0;
  return BATCHES.get(layer)?.revision ?? 0;
}

/**
 * Force any queued changes for `layer` to be applied now, resolving once complete.
 * Intended for tests and for callers that need the style settled before continuing.
 */
export function flushStyleLayerVisibility(
  layer: BatchableStyleLayer | null | undefined
): Promise<void> {
  if (!layer) return Promise.resolve();
  const state = BATCHES.get(layer);
  if (!state) return Promise.resolve();

  if (state.pending.size > 0) {
    const batch = new Map(state.pending);
    state.pending.clear();
    state.scheduled = false;
    state.chain = state.chain
      .then(() => flushBatch(layer, batch))
      .catch(() => {
        /* handled in flushBatch */
      });
  }
  return state.chain;
}

/** Discard queued changes without applying them. Use when a layer is being torn down. */
export function cancelStyleLayerVisibility(
  layer: BatchableStyleLayer | null | undefined
): void {
  if (!layer) return;
  BATCHES.get(layer)?.pending.clear();
}
